/**
 * Automatic Component Registration System
 *
 * Discovers and registers all React components in the ./components folder
 * as Web Components automatically.
 *
 * Naming Convention:
 * - Component file: BreadcrumbNav.tsx → Web Component tag: react-breadcrumb-nav
 * - Component file: DataTable.tsx → Web Component tag: react-datatable
 * - Component file: ConfirmationModal.tsx → Web Component tag: react-confirmation-modal
 */

import React from 'react';
import { logger } from '@/application/core';
import { registerReactComponents } from './web-components';

/**
 * Convert PascalCase component name to kebab-case tag name
 * @example "BreadcrumbNav" → "breadcrumb-nav"
 */
function componentNameToTagName(componentName: string): string {
  return (
    componentName
      // Insert hyphens before capital letters
      .replace(/([A-Z])/g, '-$1')
      // Remove leading hyphen and convert to lowercase
      .replace(/^-/, '')
      .toLowerCase()
  );
}

/**
 * Configuration for component registration
 */
export interface ComponentRegistryConfig {
  /** Prefix for all web component tags (default: "react-") */
  tagPrefix?: string;
  /** List of component names to exclude from registration */
  exclude?: string[];
  /** Custom tag name mappings: componentName → tagName */
  customTagNames?: Record<string, string>;
}

/**
 * Automatically discover and register all components from the components folder
 */
export function autoRegisterComponents(config: ComponentRegistryConfig = {}): void {
  const { tagPrefix = 'react-', exclude = [], customTagNames = {} } = config;

  try {
    // Use Webpack's require.context to dynamically import all component files
    // This will be replaced at build time with the actual component modules
    const componentContext = require.context(
      './components',
      true, // Include subdirectories
      /^\.\/(?!.*(stories|test|spec)).*\.tsx$/ // Match .tsx files, exclude any files containing stories, test, or spec
    );

    const componentsToRegister: Record<string, React.ComponentType<any>> = {};

    componentContext.keys().forEach((filePath: string) => {
      // Extract component name from file path
      const matches = filePath.match(/\/([^/]+)\.tsx$/);
      if (!matches) return;

      const componentName = matches[1];

      // Skip if in exclude list
      if (exclude.includes(componentName)) {
        return;
      }

      // Skip UI primitives (they're not meant to be registered as web components)
      if (filePath.includes('/ui/')) {
        return;
      }

      try {
        const module = componentContext(filePath);

        // Get the default export or named export matching the component name
        const Component = module.default || module[componentName];

        if (!Component) {
          logger.warn('ComponentRegistry', `No component found in ${filePath}`);
          return;
        }

        // Determine tag name
        let tagName: string;
        if (customTagNames[componentName]) {
          tagName = customTagNames[componentName];
        } else {
          const kebabName = componentNameToTagName(componentName);
          tagName = `${tagPrefix}${kebabName}`;
        }

        // Ensure tag name has a hyphen (required for custom elements)
        if (!tagName.includes('-')) {
          logger.warn(
            'ComponentRegistry',
            `Skipping ${componentName}: tag name "${tagName}" must contain a hyphen`
          );
          return;
        }

        componentsToRegister[tagName] = Component;
      } catch (error) {
        logger.error('ComponentRegistry', `Error loading component ${componentName}:`, error);
      }
    });

    // Register all discovered components
    registerReactComponents(componentsToRegister);
  } catch (error) {
    logger.error('ComponentRegistry', 'Failed to auto-register components:', error);
    throw error;
  }
}

/**
 * Get list of all discovered components
 * Useful for debugging and documentation
 */
export function getDiscoveredComponents(): string[] {
  try {
    const componentContext = require.context(
      './components',
      true,
      /^\.\/(?!.*(stories|test|spec)).*\.tsx$/
    );

    return componentContext
      .keys()
      .map((filePath: string) => {
        const matches = filePath.match(/\/([^/]+)\.tsx$/);
        return matches ? matches[1] : null;
      })
      .filter(Boolean) as string[];
  } catch (error) {
    logger.error('ComponentRegistry', 'Error discovering components:', error);
    return [];
  }
}
